import type {
  AnalyticsQuery,
  NLQueryResponse,
  ExecuteQueryResponse,
  Filter,
  SavedCard,
  Dashboard,
  CatalogResponse,
} from '../types/analytics.types';
import {
  MOCK_COUNT_RESULT,
  MOCK_BREAKDOWN_RESULT,
  MOCK_TREND_RESULT,
  MOCK_LIST_RESULT,
  MOCK_DASHBOARDS,
  MOCK_SAVED_CARDS,
  MOCK_FILTER_OPTIONS,
  SUGGESTED_QUERIES,
  VBC_MOCK_RESULTS,
} from './mockData';
import { applyAuthHeaders } from '@/lib/authHeaders';
import axios, { AxiosInstance } from 'axios';
import { useOrganizationsStore } from '@/stores/organizationsStore';

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    await applyAuthHeaders(config);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function getOrganizationId(): string {
  const orgId = useOrganizationsStore.getState().selectedOrganizationId;
  if (!orgId) {
    throw new Error('No organization selected');
  }
  return orgId;
}

const API_BASE = '/analytics';
const USE_MOCKS = false;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function translateNaturalLanguage(
  query: string
): Promise<NLQueryResponse> {
  if (USE_MOCKS) {
    await delay(800);

    const lowerQuery = query.toLowerCase();

    const matched = SUGGESTED_QUERIES.find((sq) =>
      lowerQuery.includes(sq.text.toLowerCase().split(' ').slice(0, 3).join(' '))
    );

    if (matched) {
      return {
        interpretedQuery: matched.query as AnalyticsQuery,
        confidence: 0.92,
      };
    }

    if (lowerQuery.includes('how many') || lowerQuery.includes('count')) {
      return {
        interpretedQuery: {
          title: 'Patient Count',
          filters: [],
          filterLogic: 'AND',
          output: { type: 'count' },
        },
        confidence: 0.85,
      };
    }

    if (lowerQuery.includes('list') || lowerQuery.includes('show me')) {
      return {
        interpretedQuery: {
          title: 'Patient List',
          filters: [],
          filterLogic: 'AND',
          output: { type: 'list' },
        },
        confidence: 0.78,
      };
    }

    if (lowerQuery.includes('trend') || lowerQuery.includes('over time') || lowerQuery.includes('by month')) {
      return {
        interpretedQuery: {
          title: 'Trend Analysis',
          filters: [],
          filterLogic: 'AND',
          output: {
            type: 'trend',
            timeRange: {
              start: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
              end: new Date().toISOString(),
              granularity: 'month',
            },
          },
        },
        confidence: 0.82,
      };
    }

    if (lowerQuery.includes('by ') || lowerQuery.includes('breakdown')) {
      return {
        interpretedQuery: {
          title: 'Breakdown Analysis',
          filters: [],
          filterLogic: 'AND',
          output: { type: 'breakdown', groupBy: 'care_manager' },
        },
        confidence: 0.75,
      };
    }

    return {
      interpretedQuery: {
        title: 'Custom Query',
        filters: [],
        filterLogic: 'AND',
        output: { type: 'count' },
      },
      confidence: 0.6,
      clarificationNeeded: 'Could you be more specific about what you\'re looking for?',
    };
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.post(`${API_BASE}/nl-to-query`,
    { natural_language_query: query },
    { params: { organization_id: orgId } }
  );
  return response.data;
}

export async function executeQuery(
  query: AnalyticsQuery,
  page?: number,
  pageSize?: number
): Promise<ExecuteQueryResponse> {
  if (USE_MOCKS) {
    await delay(600);

    if (query.id && VBC_MOCK_RESULTS[query.id]) {
      return { result: VBC_MOCK_RESULTS[query.id] };
    }

    const outputType = query.output.type;

    switch (outputType) {
      case 'count':
        return { result: MOCK_COUNT_RESULT };
      case 'breakdown':
        return { result: MOCK_BREAKDOWN_RESULT };
      case 'trend':
        return { result: MOCK_TREND_RESULT };
      case 'list':
        return { result: MOCK_LIST_RESULT };
      default:
        return { result: MOCK_COUNT_RESULT };
    }
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.post(`${API_BASE}/execute`,
    { query, page, page_size: pageSize },
    { params: { organization_id: orgId } }
  );
  return response.data;
}

export async function getPreviewCount(filters: Filter[]): Promise<number> {
  if (USE_MOCKS) {
    await delay(300);
    const baseCount = 2847;
    const reduction = filters.length * Math.floor(Math.random() * 500 + 200);
    return Math.max(0, baseCount - reduction);
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.post(`${API_BASE}/preview-count`,
    { filters },
    { params: { organization_id: orgId } }
  );
  return response.data.count;
}

export async function getCatalog(): Promise<CatalogResponse> {
  const orgId = getOrganizationId();
  const response = await axiosInstance.get(`${API_BASE}/catalog`, {
    params: { organization_id: orgId },
  });
  return response.data;
}

export async function getFilterOptions(
  field: string
): Promise<Array<{ value: string; label: string }>> {
  if (USE_MOCKS) {
    await delay(200);
    return MOCK_FILTER_OPTIONS[field as keyof typeof MOCK_FILTER_OPTIONS] || [];
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.get(`${API_BASE}/filter-options/${field}`, {
    params: { organization_id: orgId },
  });
  return response.data;
}

export async function getSavedCards(): Promise<SavedCard[]> {
  if (USE_MOCKS) {
    await delay(200);
    return MOCK_SAVED_CARDS;
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.get(`${API_BASE}/cards`, {
    params: { organization_id: orgId },
  });
  return response.data;
}

export async function saveCard(
  card: Omit<SavedCard, 'id' | 'createdAt' | 'updatedAt'>
): Promise<SavedCard> {
  if (USE_MOCKS) {
    await delay(300);
    const newCard: SavedCard = {
      ...card,
      id: `card-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    MOCK_SAVED_CARDS.push(newCard);
    return newCard;
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.post(`${API_BASE}/cards`, card, {
    params: { organization_id: orgId },
  });
  return response.data;
}

export async function updateCard(
  id: string,
  updates: Partial<SavedCard>
): Promise<SavedCard> {
  if (USE_MOCKS) {
    await delay(200);
    const index = MOCK_SAVED_CARDS.findIndex((c) => c.id === id);
    if (index !== -1) {
      MOCK_SAVED_CARDS[index] = {
        ...MOCK_SAVED_CARDS[index],
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      return MOCK_SAVED_CARDS[index];
    }
    throw new Error('Card not found');
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.patch(`${API_BASE}/cards/${id}`, updates, {
    params: { organization_id: orgId },
  });
  return response.data;
}

export async function deleteCard(id: string): Promise<void> {
  if (USE_MOCKS) {
    await delay(200);
    const index = MOCK_SAVED_CARDS.findIndex((c) => c.id === id);
    if (index !== -1) {
      MOCK_SAVED_CARDS.splice(index, 1);
    }
    return;
  }

  const orgId = getOrganizationId();
  await axiosInstance.delete(`${API_BASE}/cards/${id}`, {
    params: { organization_id: orgId },
  });
}

export async function getDashboards(): Promise<Dashboard[]> {
  if (USE_MOCKS) {
    await delay(200);
    return MOCK_DASHBOARDS;
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.get(`${API_BASE}/dashboards`, {
    params: { organization_id: orgId },
  });
  return response.data;
}

export async function createDashboard(name: string): Promise<Dashboard> {
  if (USE_MOCKS) {
    await delay(300);
    const newDashboard: Dashboard = {
      id: `dash-${Date.now()}`,
      name,
      isDefault: false,
      cards: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    MOCK_DASHBOARDS.push(newDashboard);
    return newDashboard;
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.post(`${API_BASE}/dashboards`, { name }, {
    params: { organization_id: orgId },
  });
  return response.data;
}

export async function getDashboard(id: string): Promise<Dashboard | null> {
  if (USE_MOCKS) {
    await delay(200);
    const dashboard = MOCK_DASHBOARDS.find((d) => d.id === id);
    if (!dashboard) return null;
    const cards = MOCK_SAVED_CARDS.filter((c) => c.dashboardId === id);
    return { ...dashboard, cards };
  }

  const orgId = getOrganizationId();
  try {
    const response = await axiosInstance.get(`${API_BASE}/dashboards/${id}`, {
      params: { organization_id: orgId },
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function updateDashboard(
  id: string,
  updates: Partial<Pick<Dashboard, 'name' | 'isDefault'>>
): Promise<Dashboard> {
  if (USE_MOCKS) {
    await delay(200);
    const index = MOCK_DASHBOARDS.findIndex((d) => d.id === id);
    if (index === -1) throw new Error('Dashboard not found');

    if (updates.isDefault) {
      MOCK_DASHBOARDS.forEach((d) => (d.isDefault = false));
    }

    MOCK_DASHBOARDS[index] = {
      ...MOCK_DASHBOARDS[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    const cards = MOCK_SAVED_CARDS.filter((c) => c.dashboardId === id);
    return { ...MOCK_DASHBOARDS[index], cards };
  }

  const orgId = getOrganizationId();
  const response = await axiosInstance.patch(`${API_BASE}/dashboards/${id}`, updates, {
    params: { organization_id: orgId },
  });
  return response.data;
}

export async function deleteDashboard(id: string): Promise<void> {
  if (USE_MOCKS) {
    await delay(200);
    const index = MOCK_DASHBOARDS.findIndex((d) => d.id === id);
    if (index !== -1) {
      MOCK_DASHBOARDS.splice(index, 1);
      const cardIndicesToRemove = MOCK_SAVED_CARDS
        .map((c, i) => (c.dashboardId === id ? i : -1))
        .filter((i) => i !== -1)
        .reverse();
      cardIndicesToRemove.forEach((i) => MOCK_SAVED_CARDS.splice(i, 1));
    }
    return;
  }

  const orgId = getOrganizationId();
  await axiosInstance.delete(`${API_BASE}/dashboards/${id}`, {
    params: { organization_id: orgId },
  });
}

export async function updateCardPositions(
  dashboardId: string,
  positions: Array<{ cardId: string; position: { x: number; y: number; w: number; h: number } }>
): Promise<void> {
  if (USE_MOCKS) {
    await delay(200);
    positions.forEach(({ cardId, position }) => {
      const card = MOCK_SAVED_CARDS.find((c) => c.id === cardId);
      if (card && card.dashboardId === dashboardId) {
        card.position = position;
        card.updatedAt = new Date().toISOString();
      }
    });
    return;
  }

  const orgId = getOrganizationId();
  await axiosInstance.put(`${API_BASE}/dashboards/${dashboardId}/positions`, { positions }, {
    params: { organization_id: orgId },
  });
}
